"use client";

import Link from "next/link";
import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";
import { PLAYGROUND_LANGUAGES } from "../config/languages";
import type { PlaygroundLanguage } from "../config/languages";
import type { LanguagePlaygroundContent } from "../content/types";
import { defaultVersionLabel, fillVersion } from "../lib/language-seo";
import { cn } from "@/lib/utils";

type Props = {
  locale: string;
  language: PlaygroundLanguage;
  content: LanguagePlaygroundContent;
};

export function LanguageInfoSection({ locale, language, content }: Props) {
  const t = useTranslations("playground");
  const uiLocale = useLocale();
  const loc = uiLocale === "bn" ? "bn" : "en";

  // `{version}` in any prose is the contract's default version, never a typed
  // number (`docs/24` §11.3; `lib/language-seo.ts`).
  const version = defaultVersionLabel(language);
  const fill = (text: string) => fillVersion(text, version);
  const aboutParas = content.about[loc].map(fill);
  const steps = content.howToUse[loc].map(fill);

  const related = PLAYGROUND_LANGUAGES.filter((l) => l.id !== language.id);

  return (
    <div className="space-y-10">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
          {t("seo.languageH1", { language: language.name })}
        </h1>
        <p className="text-sm text-white/55">{t("seo.languageIntroLine", { language: language.name })}</p>
      </header>

      <section aria-labelledby="pg-about" className="space-y-4">
        <h2 id="pg-about" className="text-lg font-semibold text-[#FFAB8F]">
          {t("seoAboutTitle", { language: language.name })}
        </h2>
        <div className="space-y-3 text-sm leading-relaxed text-white/80">
          {aboutParas.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      <section aria-labelledby="pg-how" className="space-y-4">
        <h2 id="pg-how" className="text-lg font-semibold text-[#FFAB8F]">
          {t("seoHowToTitle")}
        </h2>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-white/80">
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="pg-faq" className="space-y-3">
        <h2 id="pg-faq" className="text-lg font-semibold text-[#FFAB8F]">
          {t("seoFaqTitle")}
        </h2>
        <div className="divide-y divide-white/10 rounded-xl border border-white/10 bg-[#141416]/80">
          {content.faq.map((item, i) => {
            const q = fill(item.question[loc]);
            const a = fill(item.answer[loc]);
            return (
              <details key={i} className="group px-4 py-3">
                <summary
                  className={cn(
                    "cursor-pointer list-none font-medium text-white/95 outline-none",
                    "marker:content-none [&::-webkit-details-marker]:hidden",
                  )}
                >
                  <span className="inline-flex w-full items-center justify-between gap-2">
                    {q}
                    <span className="text-white/40 transition group-open:rotate-90">›</span>
                  </span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{a}</p>
              </details>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="pg-related" className="space-y-3">
        <h2 id="pg-related" className="text-lg font-semibold text-[#FFAB8F]">
          {t("seoRelatedTitle")}
        </h2>
        <ul className="flex flex-wrap gap-2">
          {related.map((l) => (
            <li key={l.id}>
              <Link
                href={`/${locale}/playground/${l.seoSlug}`}
                className="inline-flex rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/90 transition hover:border-[#E94D1D]/40 hover:bg-[#E94D1D]/10"
              >
                {l.name}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
